import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Spin,
  Alert,
  Typography,
  Tag,
  Switch,
  Space,
  Tooltip,
} from 'antd';
import { getPathologyAccuracy, getPathologyStudies } from '../services/api';
import ClassificationTag from '../components/ClassificationTag';
import { useBranch } from '../components/BranchContext';

const { Text } = Typography;

const MONO = {
  lightest: '#D6E4F7',
  light: '#7FA3D4',
  medium: '#4A72A8',
  dark: '#1A3C7A',
};

const Pathology = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOutOfScope, setShowOutOfScope] = useState(false);
  const { branchParam } = useBranch();

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const params = branchParam ? { branch: branchParam } : {};
        const result = await getPathologyAccuracy(showOutOfScope, params);
        const items = result.pathologies || result.data || [];
        const sorted = items.sort((a, b) => (b.total || 0) - (a.total || 0));
        setData(sorted);
      } catch (err) {
        setError(err.message || 'Failed to load pathology data');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [showOutOfScope, branchParam]);

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /><div style={{ marginTop: 16 }}>Loading pathology data...</div></div>;
  if (error) return <Alert type="error" message="Error" description={error} showIcon style={{ borderRadius: 12 }} />;

  const columns = [
    {
      title: 'Pathology',
      dataIndex: 'pathology',
      key: 'pathology',
      sorter: (a, b) => a.pathology.localeCompare(b.pathology),
      render: (text, record) => (
        <Space>
          <Text strong style={{ textTransform: 'capitalize', color: record.is_in_scope === false ? '#bbb' : undefined }}>
            {text}
          </Text>
          {record.is_in_scope === false && (
            <Tooltip title="This pathology is outside the model's defined scope for this study type">
              <Tag style={{ background: '#f5f5f5', color: '#aaa', border: '1px dashed #ddd', borderRadius: 6, fontSize: 11, cursor: 'help' }}>
                out of scope
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Studies',
      dataIndex: 'total',
      key: 'total',
      width: 90,
      sorter: (a, b) => (a.total || 0) - (b.total || 0),
      defaultSortOrder: 'descend',
      render: (val) => <Text strong>{val}</Text>,
    },
  ];

  return (
    <div>
      {/* Table */}
      <Card
        title={`All Pathologies (${data.length})`}
        size="small"
        style={{ borderRadius: 14, border: 'none', boxShadow: '0 1px 4px rgba(26,60,122,0.08)' }}
        headStyle={{ border: 'none', color: MONO.dark, fontWeight: 600 }}
        extra={
          <Space>
            <Text style={{ color: '#8895a7', fontSize: 12 }}>Show out of scope</Text>
            <Switch size="small" checked={showOutOfScope} onChange={setShowOutOfScope} />
          </Space>
        }
      >
        <Table
          dataSource={data}
          columns={columns}
          rowKey="pathology"
          size="small"
          expandable={{ expandedRowRender: (record) => <ExpandedStudies pathologyName={record.pathology} /> }}
          rowClassName={(record) => record.is_in_scope === false ? 'row-out-of-scope' : ''}
          pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} pathologies` }}
        />
      </Card>
    </div>
  );
};

const ExpandedStudies = ({ pathologyName }) => {
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const { branchParam } = useBranch();

  useEffect(() => {
    const fetch = async () => {
      try {
        const params = branchParam ? { branch: branchParam } : {};
        const result = await getPathologyStudies(pathologyName, params);
        setStudies(result.studies || result.data || []);
      } catch (err) {
        console.error('Failed to load studies for', pathologyName, err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [pathologyName, branchParam]);

  if (loading) return <Spin size="small" />;

  return (
    <Table
      dataSource={studies}
      columns={[
        { title: 'Study FK', dataIndex: 'study_fk', key: 'study_fk', render: (t) => <a href={`/cases/${t}`}>{t}</a> },
        { title: 'Study Name', dataIndex: 'study_name', key: 'study_name', ellipsis: true },
        { title: 'Status', dataIndex: 'status', key: 'status', render: (s) => <Tag style={{ background: s === 'FN' ? MONO.medium : MONO.dark, color: '#fff', border: 'none', borderRadius: 8 }}>{s}</Tag> },
        { title: 'Classification', dataIndex: 'classification', key: 'classification', render: (t) => <ClassificationTag classification={t} /> },
      ]}
      rowKey="study_fk"
      size="small"
      pagination={{ pageSize: 10 }}
    />
  );
};

export default Pathology;
